import canJoinSessionForPro from "utils/canJoinSessionForPro";
import canJoinSessionForMember from "utils/canJoinSessionForMember";
import canCancelSession from "utils/canCancelSession";
import { isAIAudioSessionForAllPlan } from "components/ai-session-review/utils/getAISessionType";

export const TIME_FILTER_OPTIONS = Object.freeze({
  UPCOMING: { id: "UPCOMING", value: "Upcoming session" },
  PAST: { id: "PAST", value: "Past sessions" },
} as const);

export const TYPE_FILTER_OPTIONS = Object.freeze({
  ONE_ON_ONE: { id: "ONE_ON_ONE", value: "1:1" },
  GROUP_SESSION: { id: "GROUP_SESSION", value: "Group" },
} as const);

type ItemCommon = {
  title: string;
  date: number;
  duration: number;
  timeslotId: number;
  canJoinForPro?: boolean;
  canJoinForMember?: boolean;
  canCancelForPro?: boolean;
  canCancelForMember?: boolean;
  isPast: boolean;
  twilioUrlCode: string;
  orchidPro: OrchidProMetaData;
};

type AppointmentItem = ItemCommon & {
  typeId: "ONE_ON_ONE";
  attendees: string;
  statusFlags: AppointmentMetaData["statusFlags"];
  invoiceAvailable: string;
  invoiceTimestamp?: Date;
  patient: PatientMetaData;
  cancellationPolicy: CancellationPolicy;
  pricing: PricingData;
  cancelRefundOffer?: CancellationRefundOffer;
  invoice?: InvoiceData;
};

type GroupSessionItem = ItemCommon & {
  typeId: "GROUP_SESSION";
  description?: string;
  attendees: number;
  status: GroupSessionMetaData["status"];
  zoomMeeting: ZoomMeeting;
  patients: GroupSessionPatient[];
  speakers: GroupSessionSpeaker[];
  price: number;
  invoiceAvailable: undefined;
  invoiceTimestamp: undefined;
};

export type Item = AppointmentItem | GroupSessionItem;

export type ItemWithSuperBill = Item & {
  superbills: SuperBillMetaData[];
};

export default function getItemsByTime(
  appointments: (AppointmentMetaData & { invoice?: InvoiceData })[],
  groupSessions: GroupSessionMetaData[],
  now: number,
): Item[] {
  const past: Item[] = [];
  const upcoming: Item[] = [];

  // for appointments
  for (let index = 0, length = appointments.length; index < length; index++) {
    const curr = appointments[index];
    const start = +Date.parse(curr.appointment_start);
    const end = +Date.parse(curr.appointment_end);
    const duration = Math.round((end - start) / 1000 / 60);
    const item: AppointmentItem = {
      invoice: curr.invoice,
      orchidPro: curr.orchid_pro,
      patient: curr.patient,
      title: curr.appointment_type_label,
      date: start,
      // isPast: 30 mins after session ends
      isPast: end + 30 * 60 * 1000 <= now,
      duration,
      typeId: TYPE_FILTER_OPTIONS.ONE_ON_ONE.id,
      attendees: [curr.patient?.first_name, curr.patient?.last_name].join(" "),
      twilioUrlCode: curr.twilio_url_code,
      timeslotId: curr.appointment_id,
      canJoinForPro:
        canJoinSessionForPro(start, end) && !isAIAudioSessionForAllPlan(curr),
      canJoinForMember:
        canJoinSessionForMember(start, end) &&
        !isAIAudioSessionForAllPlan(curr),
      canCancelForPro: canCancelSession(end),
      canCancelForMember: canCancelSession(start),
      statusFlags: curr.statusFlags,
      invoiceAvailable: curr.invoice_available,
      invoiceTimestamp: curr.invoice_timestamp
        ? new Date(curr.invoice_timestamp)
        : undefined,
      cancellationPolicy: curr.cancellation_policy,
      pricing: curr.pricing,
      cancelRefundOffer: curr.cancel_refund_offer,
    };
    if (!item.isPast) {
      // upcoming
      upcoming.push(item);
    } else {
      past.push(item);
    }
  }

  // for group sessions
  for (let index = 0, length = groupSessions.length; index < length; index++) {
    const curr = groupSessions[index];
    const start = +Date.parse(curr.group_session_start);
    const end = +Date.parse(curr.group_session_end);
    const duration = Math.round((end - start) / 1000 / 60);
    const item: GroupSessionItem = {
      orchidPro: curr.orchid_pro,
      speakers: curr.speakers,
      patients: curr.patients,
      title: curr.topic,
      description: curr.description,
      price: curr.price,
      date: start,
      // isPast: 30 mins after session ends
      isPast: end + 30 * 60 * 1000 <= now,
      duration,
      typeId: TYPE_FILTER_OPTIONS.GROUP_SESSION.id,
      attendees: curr.number_of_registered_patients,
      timeslotId: curr.group_session_id,
      canJoinForPro: canJoinSessionForPro(start, end),
      canJoinForMember: canJoinSessionForMember(start, end),
      canCancelForPro: canCancelSession(end),
      canCancelForMember: canCancelSession(start),
      status: curr.status,
      zoomMeeting: curr.zoom_meeting,
      twilioUrlCode: curr.twilio_url_code,
      // perhaps we add group session receipt later
      invoiceAvailable: undefined,
      invoiceTimestamp: undefined,
    };
    if (!item.isPast) {
      // upcoming
      upcoming.push(item);
    } else {
      past.push(item);
    }
  }

  // sort ascending for upcoming
  upcoming.sort(({ date: a }, { date: b }) => (a === b ? 0 : a < b ? -1 : 1));
  past.sort(({ date: a }, { date: b }) => (a === b ? 0 : a > b ? -1 : 1));
  return [...upcoming, ...past];
}

export function getItemsByTimeWithSuperBill(
  appointments: AppointmentWithSuperBillMetaData[],
  groupSessions: GroupSessionMetaData[],
  now: number,
): ItemWithSuperBill[] {
  return getItemsByTime(appointments, groupSessions, now).map((item) => {
    if (item.typeId === "ONE_ON_ONE") {
      const appointment = appointments.find(
        (e) => e.appointment_id === item.timeslotId,
      );
      if (appointment) {
        return { ...item, superbills: appointment.superbills };
      }
    }
    return { ...item, superbills: [] };
  });
  // superbills: curr.superbills,
}
